import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import { useReactlytics } from '@americanexpress/reactlytics';
import InputComponent from './InputComponent';
import styles from '../styles/PageWrapper.scss';
import { ChatLanding } from './ChatLanding';
import { ChatDisplay } from './ChatDisplay';
import { CreateWishlistModal } from './CreateWishlistModal';
import { ErrorScreens } from './ErrorScreens';
import useChatScrollTracking from '../hooks/useChatScrollTracking';
import { useChatMessages } from '../hooks/useChatMessages';
import { useConversationSession } from '../hooks/useConversationSession';
import { useWishlistSave } from '../hooks/useWishlistSave';

const PageWrapper = ({ errorScreen }) => {
  const { track } = useReactlytics();
  const apiUrl = useSelector((state) => state.getIn(['config', 'apiUrl']));

  const isAutoScrollingRef = useRef(false);
  const messagesRef = useRef(null);

  const {
    conversationId,
    sessionStatus,
  } = useConversationSession({
    apiUrl,
    errorScreen,
  });

  /* istanbul ignore next */
  const scrollToBottom = () => {
    const el = messagesRef.current;
    if (!el) return;

    isAutoScrollingRef.current = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
        requestAnimationFrame(() => {
          isAutoScrollingRef.current = false;
        });
      });
    });
  };

  const {
    messageArray, // comment for local
    loadingStep,
    conversationApiStatus,
    inputValue,
    setInputValue,
    sendMessage,
    appendErrorMessage,
  } = useChatMessages({
    apiUrl,
    conversationId,
  });

  // local dev for hotel testing
  // const messageArray = mockMessageArray;

  const {
    selectedHotels,
    createModalIsOpen,
    setCreateModalIsOpen,
    saveSelectedHotels,
    isSaveSuccess,
    isSaveLoading,
  } = useWishlistSave({
    apiUrl,
    conversationId,
    track,
  });

  const handleScroll = useChatScrollTracking({
    track, isAutoScrollingRef, scrollContainerRef: messagesRef,
  });

  return (
    <>
      <div className={`${styles.bggradient} height-full flex flex-column`}>
        <div className="pad-2 body-xsmall">
          Id: {conversationId}
        </div>
        {!errorScreen && (sessionStatus === 200 || sessionStatus === null) && (
          <>
            <div
              ref={messagesRef}
              onScroll={handleScroll}
              className="container flex flex-column flex-item-grow pad-2-t scroll-y"
            >
              <h1 className="sr-only">Chat page for ai wishlist builder</h1>
              {messageArray.length === 0 && (
              <ChatLanding setInputValue={setInputValue} />
              )}
              {messageArray.length > 0
              && (
                <ChatDisplay
                  messageArray={messageArray}
                  conversationApiStatus={conversationApiStatus}
                  loadingStep={loadingStep}
                  appendErrorMessage={appendErrorMessage}
                  scrollToBottom={scrollToBottom}
                />
              )}
            </div>
            <InputComponent
              conversationApiStatus={conversationApiStatus}
              sendMessage={sendMessage}
              selectedHotels={selectedHotels}
              setCreateModalIsOpen={setCreateModalIsOpen}
              inputValue={inputValue}
              setInputValue={setInputValue}
            />
          </>
        )}
        {(errorScreen || (sessionStatus !== 200 && sessionStatus !== null)) && (
          <ErrorScreens errorScreen={errorScreen} sessionStatus={sessionStatus} />
        )}
      </div>
      <AnimatePresence>
        {createModalIsOpen && (
        <CreateWishlistModal
          setCreateModalIsOpen={setCreateModalIsOpen}
          saveSelectedHotels={saveSelectedHotels}
          isSaveLoading={isSaveLoading}
          isSaveSuccess={isSaveSuccess}
        />
        )}
      </AnimatePresence>
    </>
  );
};

export default PageWrapper;

PageWrapper.propTypes = {
  errorScreen: PropTypes.string,
};
